import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheck } from 'lucide-react';

import { webEncounterCapture, bindCaptureTransport } from '../../../lib/encounterCapture';
import { decide } from '../../../lib/captureEligibility';

/**
 * M4 — governed capture control on the Business Web APPOINTMENT DETAIL.
 *
 * The counterpart of the control Android's and iOS's scheduling screens have had since M4 landed.
 * Without it the Web plane could only start a recording from inside an active call, which leaves two
 * real consultations unrecordable: an in-person encounter in the clinic room, where there is no call
 * to open the control from, and a remote one the provider wants running before they join. Both
 * phones can do this today; Web could not, and that gap is what this closes.
 *
 * IT DECIDES NOTHING. `capture_readiness` is already on the detail the page is rendering — the
 * server computed it from profile applicability, entitlement, role, consent policy and the patient's
 * actual consent — and `decide()` turns that into a control state. A missing or unreadable readiness
 * produces a refusal, never an offer.
 *
 * IT SHARES ONE PRODUCER WITH THE CALL SURFACE. `webEncounterCapture` is the whole plane's single
 * recorder, so a capture started here is already running when the provider joins and Unified Desktop
 * mounts, and a stop from either surface is seen by both. That shared state is the reason this
 * component subscribes rather than holding a `running` flag of its own.
 */
const AppointmentCaptureControl = ({
  appointmentId,
  businessId,
  readiness,              // the detail's `capture_readiness` map
  captureApi,             // { sendSegment } — the ONE transport
  callStream = null,      // the call's local stream when a call is already up on this page
}) => {
  // Memoised on the readiness object itself: `decide` is pure, and a fresh decision on every
  // render would re-run the withdrawal check below for no reason.
  const decision = useMemo(() => decide(readiness, 'audio'), [readiness]);
  // WHICH appointment is recording, not merely whether this one is. A boolean would go unchanged
  // when a DIFFERENT appointment started or stopped, so React would skip the re-render and the
  // "another consultation is being recorded" line below would be stale.
  const [recordingAppointment, setRecordingAppointment] = useState(
    () => webEncounterCapture.runningAppointmentId(),
  );
  const [refusal, setRefusal] = useState(null);
  const running = recordingAppointment === appointmentId && recordingAppointment !== '';

  // The producer is the authority on whether a recording is in progress; this only mirrors it. A
  // capture started here and then stopped from the call surface has to stop reading as running here
  // too, and the subscription is what makes that automatic rather than remembered.
  useEffect(() => {
    bindCaptureTransport(captureApi && captureApi.sendSegment);
    setRecordingAppointment(webEncounterCapture.runningAppointmentId());
    return webEncounterCapture.subscribe(() => {
      setRecordingAppointment(webEncounterCapture.runningAppointmentId());
    });
  }, [appointmentId, captureApi]);

  // Readiness is a CONTINUOUSLY granted permission. A refreshed detail that no longer permits
  // capture must STOP a recording in progress, not merely disable the button.
  useEffect(() => {
    if (webEncounterCapture.runningAppointmentId() !== appointmentId) return;
    void webEncounterCapture.onReadinessChanged(decision);
  }, [appointmentId, decision]);

  // Deliberately NOT stopping on unmount. Closing the detail drawer to go and join the consultation
  // is the normal path, and a recording that ended because a panel closed would be the defect.
  // Teardown belongs to the call surface's disconnect and to an explicit Stop.

  const onToggle = useCallback(() => {
    (async () => {
      setRefusal(null);
      if (webEncounterCapture.isRunning()) {
        await webEncounterCapture.stop();
        return;
      }
      const started = await webEncounterCapture.start({
        businessId,
        appointmentId,
        decision,
        operatorRequested: true,   // a person asked — the only thing that may start a capture
        // When a call is already up, its microphone is the source and no second one is opened.
        // With no call, the producer opens the provider's mic, as both mobile planes do.
        stream: callStream || null,
      });
      if (!started) setRefusal(webEncounterCapture.lastRefusal);
    })();
  }, [appointmentId, businessId, decision, callStream]);

  // The server said nothing about audio capture for this appointment: no control at all.
  if (!decision.offer) return null;

  // Another appointment is being recorded. Offering Start here would silently stop that one, so the
  // control says what is true instead.
  const busyElsewhere = recordingAppointment !== '' && !running;

  return (
    <div data-testid="appointment-capture-control" className="border-t pt-2">
      <div className="text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
        <ShieldCheck className="h-3.5 w-3.5" />Recording
      </div>
      {decision.enabled && !busyElsewhere ? (
        <>
          <button
            type="button"
            data-testid="appointment-capture-toggle"
            onClick={onToggle}
            className={`w-full rounded-md px-3 py-1.5 text-xs font-semibold text-white ${
              running ? 'bg-red-700 hover:bg-red-800' : 'bg-slate-600 hover:bg-slate-700'}`}
          >
            {running ? 'Stop recording' : 'Start recording'}
          </button>
          {running && (
            <p data-testid="appointment-capture-running" className="mt-1 text-xs text-gray-500">
              Recording this consultation. It continues when you join the call.
            </p>
          )}
          {refusal && !running && (
            <p data-testid="appointment-capture-refusal" className="mt-1 text-xs text-red-600">
              {refusal}
            </p>
          )}
        </>
      ) : (
        <>
          {/* Truthfully unavailable, with the governed reason — never a dead button. */}
          <p className="text-xs text-gray-500">Recording is currently unavailable.</p>
          {busyElsewhere ? (
            <p data-testid="appointment-capture-busy" className="mt-0.5 text-xs text-gray-400">
              Another consultation is being recorded.
            </p>
          ) : decision.reason ? (
            <p data-testid="appointment-capture-reason" className="mt-0.5 text-xs text-gray-400">
              {decision.reason}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};

export default AppointmentCaptureControl;
